import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { GridItem } from 'uikit';
import { getLengthValidator } from 'utils';

import { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { UseControllerProps } from 'react-hook-form/dist/types/controller';

type TEditCommentModalProps = {
    modalTitle: string;
    defaultValue: string | undefined | null;
    isOpened: boolean;
    onClose: () => void;
    onSubmit: ({ value }: { value: string | null | undefined }) => void;
    btnConfirmLabel?: string;
    isCommit?: boolean;
    isLoading?: boolean;
    confirmationDialogTextHeader?: string;
    confirmationDialogTextConfirmation?: string;
    confirmationDialogCancelButtonText?: string;
    confirmationDialogConfirmButtonText?: string;
    rules?: UseControllerProps['rules'];
    inputLabel?: string;
};

const confirmationDialogTextHeaderDefault = '';
const confirmationDialogTextConfirmationDefault =
    'Вы уверены, что хотите отменить? Заполненные данные не будут сохранены';
const confirmationDialogCancelButtonTextDefault = 'Не отменять';
const confirmationDialogConfirmButtonTextDefault = 'Отменить';
const defaultValueRules = getLengthValidator(true);

export const EditCommentModal = (props: TEditCommentModalProps) => {
    const {
        modalTitle,
        btnConfirmLabel,
        isOpened,
        onClose,
        defaultValue,
        isCommit,
        isLoading,
        onSubmit,
        confirmationDialogTextHeader = confirmationDialogTextHeaderDefault,
        confirmationDialogTextConfirmation = confirmationDialogTextConfirmationDefault,
        confirmationDialogCancelButtonText = confirmationDialogCancelButtonTextDefault,
        confirmationDialogConfirmButtonText = confirmationDialogConfirmButtonTextDefault,
        rules = defaultValueRules,
        inputLabel,
    } = props;

    const formRef = useRef<HTMLFormElement>(null);

    const {
        control,
        handleSubmit,
        formState: { isDirty },
    } = useForm({
        criteriaMode: 'all',
        shouldUnregister: true,
        defaultValues: {
            value: defaultValue,
        },
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const handleCloseModal = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [handleOpenConfirmationDialog, isDirty, onClose]);

    const onSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    return (
        <ModalDialog
            isOpen={isOpened}
            title={modalTitle}
            onConfirm={onSave}
            onClose={handleCloseModal}
            btnConfirmLabel={btnConfirmLabel}
            withTitleCloseButton
            isOnClickOutsideDisable
            btnConfirmDisabled={!isDirty}
            isLoading={isLoading}
            isCommit={isCommit}
            bodyWidth={600}
        >
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <GridItem style={{ padding: '20px' }}>
                    <ControlledTextField
                        name="value"
                        control={control}
                        type="textarea"
                        cols={50}
                        rows={8}
                        rules={rules}
                        label={inputLabel}
                        labelPosition="top"
                    />
                </GridItem>
            </form>
            <ConfirmationDialog
                textHeader={confirmationDialogTextHeader}
                textConfirmation={confirmationDialogTextConfirmation}
                cancelButtonText={confirmationDialogCancelButtonText}
                confirmButtonText={confirmationDialogConfirmButtonText}
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

EditCommentModal.displayName = 'EditCommentModal';
